import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const COOKIE_CONSENT_KEY = 'byron_cookie_consent';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: true,
      functional: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const content = {
    fr: {
      title: 'Nous utilisons des cookies',
      description: 'Ce site utilise des cookies pour améliorer votre expérience. Les cookies essentiels sont nécessaires au fonctionnement du site. Vous pouvez accepter tous les cookies ou personnaliser vos préférences.',
      acceptAll: 'Accepter tout',
      essentialOnly: 'Cookies essentiels uniquement',
      customize: 'Personnaliser',
      learnMore: 'En savoir plus',
      essentialTitle: 'Cookies essentiels',
      essentialDesc: 'Nécessaires au fonctionnement du site (authentification, sécurité, préférences)',
      analyticsTitle: 'Cookies analytiques',
      analyticsDesc: 'Nous aident à comprendre comment vous utilisez le site',
      functionalTitle: 'Cookies fonctionnels',
      functionalDesc: 'Permettent de personnaliser votre expérience',
      save: 'Enregistrer mes préférences'
    },
    en: {
      title: 'We use cookies',
      description: 'This site uses cookies to improve your experience. Essential cookies are necessary for the site to function. You can accept all cookies or customize your preferences.',
      acceptAll: 'Accept all',
      essentialOnly: 'Essential cookies only',
      customize: 'Customize',
      learnMore: 'Learn more',
      essentialTitle: 'Essential cookies',
      essentialDesc: 'Necessary for the site to function (authentication, security, preferences)',
      analyticsTitle: 'Analytics cookies',
      analyticsDesc: 'Help us understand how you use the site',
      functionalTitle: 'Functional cookies',
      functionalDesc: 'Allow us to personalize your experience',
      save: 'Save my preferences'
    }
  };

  const t = content[language] || content.fr;

  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    functional: false
  });

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-sm shadow-lg border border-[#E0DED8]">
            {!showDetails ? (
              /* Main Banner */
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="hidden md:flex w-10 h-10 bg-[#F5F3EF] rounded-full items-center justify-center flex-shrink-0">
                    <Cookie size={20} className="text-[#8B7355]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-[#1A1A1A] mb-2">{t.title}</h3>
                    <p className="text-sm text-[#6B6B6B] mb-4">{t.description}</p>
                    
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="px-5 py-2 bg-[#1A1A1A] text-white text-sm rounded-sm hover:bg-[#333] transition-colors"
                        data-testid="accept-all-cookies"
                      >
                        {t.acceptAll}
                      </button>
                      <button
                        onClick={handleAcceptEssential}
                        className="px-5 py-2 border border-[#E0DED8] text-[#6B6B6B] text-sm rounded-sm hover:bg-[#F5F3EF] transition-colors"
                        data-testid="accept-essential-cookies"
                      >
                        {t.essentialOnly}
                      </button>
                      <button
                        onClick={handleCustomize}
                        className="px-5 py-2 text-[#8B7355] text-sm hover:underline"
                        data-testid="customize-cookies"
                      >
                        {t.customize}
                      </button>
                    </div>

                    <div className="mt-4">
                      <Link 
                        to="/cookies" 
                        className="text-xs text-[#9B9B9B] hover:text-[#6B6B6B] underline"
                      >
                        {t.learnMore}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Detailed Preferences */
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-medium text-[#1A1A1A]">{t.customize}</h3>
                  <button 
                    onClick={() => setShowDetails(false)}
                    className="p-1 hover:bg-[#F5F3EF] rounded"
                  >
                    <X size={18} className="text-[#6B6B6B]" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential - Always on */}
                  <div className="flex items-start justify-between p-4 bg-[#F5F3EF] rounded-sm">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-sm text-[#1A1A1A]">{t.essentialTitle}</p>
                      <p className="text-xs text-[#6B6B6B] mt-1">{t.essentialDesc}</p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        className="w-4 h-4 text-[#8B7355] rounded border-[#E0DED8]"
                      />
                      <span className="ml-2 text-xs text-[#9B9B9B]">
                        {language === 'fr' ? 'Requis' : 'Required'}
                      </span>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between p-4 border border-[#E0DED8] rounded-sm">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-sm text-[#1A1A1A]">{t.analyticsTitle}</p>
                      <p className="text-xs text-[#6B6B6B] mt-1">{t.analyticsDesc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="w-4 h-4 text-[#8B7355] rounded border-[#E0DED8] focus:ring-[#8B7355]"
                    />
                  </div>

                  {/* Functional */}
                  <div className="flex items-start justify-between p-4 border border-[#E0DED8] rounded-sm">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-sm text-[#1A1A1A]">{t.functionalTitle}</p>
                      <p className="text-xs text-[#6B6B6B] mt-1">{t.functionalDesc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                      className="w-4 h-4 text-[#8B7355] rounded border-[#E0DED8] focus:ring-[#8B7355]"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSavePreferences}
                  className="w-full px-5 py-2 bg-[#1A1A1A] text-white text-sm rounded-sm hover:bg-[#333] transition-colors"
                  data-testid="save-cookie-preferences"
                >
                  {t.save}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
